import React, { useCallback, useLayoutEffect, useRef } from "react";
import { ArrowLeft } from "lucide-react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { cn, useTheme } from "@/ui/theme/ThemeContext";
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { buildAgentOfficeEmbedUrl, buildAgentOfficeProjectRoute } from "./agentOfficeLaunch";

const AGENT_OFFICE_PROJECT_SELECTED_MESSAGE = "agent-office:project-selected";
const AGENT_OFFICE_PROJECT_AGENT_SELECTED_MESSAGE = "agent-office:project-agent-selected";
const AGENT_OFFICE_PROJECT_CONTEXT_MESSAGE = "agent-office:project-context";
const AGENT_OFFICE_SHOW_PROJECT_FILE_MESSAGE = "agent-office:show-project-file";

interface AgentOfficeFrameProps {
  projectId: string;
  initialProjectAgentId: string | null;
  projectDisplayName: string | null;
  onLoad: () => void;
}

interface ProjectDisplayContext {
  projectId: string;
  projectName: string;
}

const AgentOfficeFrame = React.forwardRef<HTMLIFrameElement, AgentOfficeFrameProps>(
  ({ projectId, initialProjectAgentId, projectDisplayName, onLoad }, ref) => {
    const { t } = useSettings();
    const [src] = React.useState(() =>
      buildAgentOfficeEmbedUrl(projectId, initialProjectAgentId),
    );
    return (
      <iframe
        ref={ref}
        data-testid="agent-office-embed-frame"
        title={projectDisplayName ? t('Agent Office for {{projectName}}', { projectName: projectDisplayName }) : t('Agent Office project workspace')}
        src={src}
        onLoad={onLoad}
        className="h-full min-h-0 w-full border-0"
        allow="fullscreen"
        allowFullScreen
      />
    );
  },
);
AgentOfficeFrame.displayName = "AgentOfficeFrame";

export const AgentOfficeEmbedPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { t } = useSettings();
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [projectDisplayContext, setProjectDisplayContext] = React.useState<ProjectDisplayContext | null>(null);
  const projectDisplayName = projectDisplayContext && projectDisplayContext.projectId === projectId
    ? projectDisplayContext.projectName
    : null;
  const routeProjectAgentId = new URLSearchParams(location.search)
    .get("projectAgentId")
    ?.trim() || null;

  // A revision changes for external selections and document replacement. Echoes update this
  // cursor before navigating, so they never reset the iframe or its selection.
  const selection = useRef({ projectId: "", projectAgentId: null as string | null, revision: 0 });
  const pendingNavigation = useRef(false);
  const documentBinding = useRef<{ document: Document; documentId: string | null; needsSync: boolean } | null>(null);
  const readFrame = useCallback(() => {
    const child = frameRef.current?.contentWindow;
    if (!child) return null;
    try {
      const url = new URL(child.location.href);
      if (url.origin !== window.location.origin
        || !["/agent-office/", "/agent-office/index.html"].includes(url.pathname)
        || url.searchParams.get("source") !== "pm") return null;
      const document = child.document;
      const documentId = document.documentElement?.dataset.agentOfficeSelectionDocument || null;
      const changed = documentBinding.current?.document !== document;
      if (changed) {
        const replacingDocument = Boolean(documentBinding.current);
        if (replacingDocument) selection.current.revision += 1;
        documentBinding.current = { document, documentId, needsSync: replacingDocument };
      } else if (documentId && !documentBinding.current!.documentId) {
        documentBinding.current!.documentId = documentId;
        documentBinding.current!.needsSync = true;
      }
      // A revisioned document cannot silently downgrade by losing its marker.
      return { child, url, needsSync: documentBinding.current!.needsSync, documentId: documentBinding.current!.documentId };
    } catch {
      return null; // Cross-origin navigation or a document that is not ready.
    }
  }, []);
  const sendSelection = useCallback((forceLegacy = false) => {
    if (!selection.current.projectId) return;
    if (forceLegacy) pendingNavigation.current = true;
    const frame = readFrame();
    if (!frame || frame.url.searchParams.get("projectId") !== selection.current.projectId) return;
    if (frame.documentId) {
      frame.child.postMessage({
        type: "agent-office:set-project-agent",
        ...selection.current,
        documentId: frame.documentId,
      }, window.location.origin);
      documentBinding.current!.needsSync = false;
      pendingNavigation.current = false;
      return;
    }
    // Deployed pre-bridge Offices already consume history + popstate. Use that
    // same-origin route API without navigating the document or touching drafts.
    if (pendingNavigation.current || frame.needsSync) {
      if (selection.current.projectAgentId) frame.url.searchParams.set("projectAgentId", selection.current.projectAgentId);
      else frame.url.searchParams.delete("projectAgentId");
      frame.child.history.replaceState(frame.child.history.state, "", frame.url.href);
      frame.child.dispatchEvent(new PopStateEvent("popstate"));
    }
    documentBinding.current!.needsSync = false;
    pendingNavigation.current = false;
  }, [readFrame]);

  useLayoutEffect(() => {
    if (selection.current.projectId !== projectId || selection.current.projectAgentId !== routeProjectAgentId) {
      const isNavigation = Boolean(selection.current.projectId);
      selection.current = { projectId: projectId ?? "", projectAgentId: routeProjectAgentId, revision: selection.current.revision + 1 };
      sendSelection(isNavigation);
    }
  }, [projectId, routeProjectAgentId, sendSelection]);

  useLayoutEffect(() => {
    const onMessage = (event: MessageEvent<unknown>) => {
      if (event.origin !== window.location.origin) return;
      if (event.source !== frameRef.current?.contentWindow) return;
      if (!event.data || typeof event.data !== "object") return;

      const message = event.data as Record<string, unknown>;
      if (message.type === "agent-office:selection-ready") {
        const frame = readFrame();
        if (frame?.documentId && message.documentId === frame.documentId
          && message.projectId === projectId) sendSelection();
        return;
      }
      if (message.type === AGENT_OFFICE_PROJECT_CONTEXT_MESSAGE) {
        if (typeof message.projectId !== "string" || message.projectId.trim() !== projectId) return;
        if (typeof message.projectName !== "string") return;
        const nextProjectDisplayName = message.projectName.trim();
        if (
          !nextProjectDisplayName
          || nextProjectDisplayName === projectId
          || /^[a-f\d]{24}$/i.test(nextProjectDisplayName)
        ) return;
        setProjectDisplayContext({ projectId, projectName: nextProjectDisplayName });
        return;
      }
      if (message.type === AGENT_OFFICE_PROJECT_SELECTED_MESSAGE) {
        if (typeof message.projectId !== "string") return;
        const nextProjectId = message.projectId.trim();
        if (!nextProjectId || nextProjectId === projectId) return;
        navigate(buildAgentOfficeProjectRoute(nextProjectId));
        return;
      }
      if (message.type === AGENT_OFFICE_SHOW_PROJECT_FILE_MESSAGE) {
        if (typeof message.projectId !== "string" || message.projectId.trim() !== projectId) return;
        if (typeof message.projectFileId !== "string") return;
        const projectFileId = message.projectFileId.trim();
        if (!projectFileId) return;
        navigate(`/?view=docs&projectId=${encodeURIComponent(projectId)}&projectFileId=${encodeURIComponent(projectFileId)}`);
        return;
      }
      if (message.type !== AGENT_OFFICE_PROJECT_AGENT_SELECTED_MESSAGE) return;
      const frame = readFrame();
      if (!frame || frame.url.searchParams.get("projectId") !== projectId) return;
      if (frame.needsSync || pendingNavigation.current) sendSelection(true);
      if (frame.documentId) {
        if (message.documentId !== frame.documentId || message.revision !== selection.current.revision) return;
      } else {
        // Legacy messages have no revision. The live child URL is authoritative:
        // it was updated by selectAgent before enqueueing its message, and by us
        // on external navigation. This rejects queued selections from an old URL.
        if (message.revision !== undefined || message.documentId !== undefined) return;
        const childId = new URL(frame.child.location.href).searchParams.get("projectAgentId")?.trim() || null;
        if (childId !== (typeof message.projectAgentId === "string" ? message.projectAgentId.trim() || null : message.projectAgentId)) return;
      }
      if (typeof message.projectId !== "string" || message.projectId.trim() !== projectId) return;
      if (message.projectAgentId !== null && typeof message.projectAgentId !== "string") return;

      const nextProjectAgentId = String(message.projectAgentId ?? "").trim() || null;
      if (nextProjectAgentId === selection.current.projectAgentId) return;
      selection.current.projectAgentId = nextProjectAgentId;
      const params = new URLSearchParams(location.search);
      if (nextProjectAgentId) params.set("projectAgentId", nextProjectAgentId);
      else params.delete("projectAgentId");
      navigate({ pathname: location.pathname, search: params.toString(), hash: location.hash }, { replace: true });
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [navigate, projectId, location.pathname, location.search, location.hash, sendSelection, readFrame]);

  if (!projectId) {
    return (
      <main
        className={cn(
          "flex h-screen items-center justify-center px-6",
          theme.bgPrimary,
          theme.textPrimary,
        )}
      >
        <div className="text-center">
          <h1 className="text-lg font-semibold">{t('Agent Office cannot open')}</h1>
          <p className={cn("mt-2 text-sm", theme.textSecondary)}>
            {t('Select an active Project Manager project before opening Agent Office.')}
          </p>
          <Link
            to="/"
            className={cn(
              "mt-4 inline-flex rounded-md px-3 py-2 text-sm font-medium",
              theme.primaryAction,
            )}
          >
            {t('Back to Project Manager')}
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main
      className={cn(
        "grid h-screen min-w-0 grid-rows-[auto_minmax(0,1fr)]",
        theme.bgPrimary,
        theme.textPrimary,
      )}
    >
      <header className={cn("flex items-center border-b px-3 py-2", theme.borderPrimary)}>
        <Link
          to="/"
          aria-label={t('Back to Project Manager')}
          className={cn(
            "inline-flex shrink-0 items-center gap-2 rounded-md px-3 py-2 text-sm font-medium",
            theme.surfaceHover,
          )}
        >
          <ArrowLeft aria-hidden="true" size={16} />
          <span>{t('Back to Project Manager')}</span>
        </Link>
        <div className="ml-4 min-w-0">
          <h1 className="truncate text-sm font-semibold">{t('Agent Office')}</h1>
          <p className={cn("truncate text-xs", theme.textMuted)}>{projectDisplayName || t('Project workspace')}</p>
        </div>
      </header>
      <AgentOfficeFrame
        key={projectId}
        ref={frameRef}
        projectId={projectId}
        initialProjectAgentId={routeProjectAgentId}
        projectDisplayName={projectDisplayName}
        onLoad={() => sendSelection()}
      />
    </main>
  );
};
